import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH19_QUESTIONS } from "../data/neetPhysicsCh19Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 19: Moving Charges and Magnetism (NCERT Class 12).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Magnetic Force on Charges", "Motion in a Magnetic Field", "Biot-Savart Law", "Ampere's Circuital Law", "Torque, Galvanometer & Force Between Currents"];

const SECTION_COLORS = {
  "Magnetic Force on Charges":                          "#2563eb",
  "Motion in a Magnetic Field":                          "#7c3aed",
  "Biot-Savart Law":                                     "#dc2626",
  "Ampere's Circuital Law":                              "#16a34a",
  "Torque, Galvanometer & Force Between Currents":       "#ea580c",
};

const CONCEPTS = [
  // ── Magnetic Force on Charges ───────────────────────────────────────────────
  { section: "Magnetic Force on Charges", title: "Lorentz force on a moving charge (F = qv×B)",
    explanation: "A charge q moving with velocity v through a magnetic field B experiences a force F = qv×B. Its magnitude is F = qvBsinθ, where θ is the angle between v and B, and its direction is perpendicular to BOTH v and B, given by the right-hand rule applied to v×B (reversed if q is negative). Unlike gravity or the electric force, this magnetic force depends on the charge's motion, not just its position.",
    example: `F = qvB sin(theta)

Proton: q = 1.6x10^-19 C, v = 2x10^6 m/s (perpendicular to B)
B = 0.5 T, theta = 90 deg (sin 90 = 1)

F = (1.6x10^-19)(2x10^6)(0.5)(1) = 1.6x10^-13 N` },
  { section: "Magnetic Force on Charges", title: "Special cases: force when v is parallel to B, and maximum force",
    explanation: "Because F = qvBsinθ, the force vanishes when v is parallel or antiparallel to B (θ = 0 deg or 180 deg, sinθ = 0) -- the particle then moves in a straight line, unaffected by the field. The force is maximum when v is perpendicular to B (θ = 90 deg, sinθ = 1), giving F = qvB.",
    example: `theta = 0 deg or 180 deg  ->  sin(theta) = 0  ->  F = 0  (straight line)
theta = 90 deg            ->  sin(theta) = 1  ->  F = qvB  (maximum)` },
  { section: "Magnetic Force on Charges", title: "Combined electric and magnetic force — velocity selector",
    explanation: "When both an electric field E and a magnetic field B act on a charge, the total (Lorentz) force is F = q(E + v×B). If E and B are set up perpendicular to each other and to v, a charge experiences the electric force qE and magnetic force qvB in opposite directions; only particles with the exact speed v = E/B pass through undeflected -- this is the working principle of a velocity selector.",
    example: `Undeflected condition: qE = qvB   ->   v = E / B

E = 3x10^5 N/C, B = 0.2 T
v = (3x10^5) / (0.2) = 1.5x10^6 m/s  (only this speed passes straight through)` },
  { section: "Magnetic Force on Charges", title: "Magnetic force does no work",
    explanation: "Since F = qv×B is always perpendicular to the velocity v, the instantaneous power delivered by the magnetic force, P = F.v, is always zero. The magnetic force can change a charge's DIRECTION of motion but can never change its SPEED or kinetic energy -- this is why charged particles move in circles or helices of constant speed in a uniform magnetic field.",
    example: `F is always perpendicular to v  (since F = qv x B)
Power delivered: P = F . v = 0  (dot product of perpendicular vectors)

=> Magnetic force changes DIRECTION only, never speed or KE` },
  { section: "Magnetic Force on Charges", title: "Magnetic force on a current-carrying conductor (F = IL×B)",
    explanation: "A current-carrying wire contains many moving charges, so it experiences a net magnetic force too. Summing qv_d×B over all charge carriers in a straight segment of length L (vector along the current direction) gives F = IL×B, magnitude F = BILsinθ. The direction is given by the right-hand rule for IL×B (equivalently, Fleming's left-hand rule, the standard mnemonic for this 'motor effect').",
    example: `F = B I L sin(theta)

I = 5 A, L = 0.4 m, B = 0.25 T, wire perpendicular to B (theta = 90 deg)
F = (0.25)(5)(0.4)(1) = 0.5 N` },

  // ── Motion in a Magnetic Field ───────────────────────────────────────────────
  { section: "Motion in a Magnetic Field", title: "Circular motion of a charged particle in a uniform magnetic field",
    explanation: "When a charged particle moves perpendicular to a uniform magnetic field, the magnetic force is always perpendicular to its velocity and constant in magnitude -- exactly the condition for uniform circular motion. Setting the magnetic force equal to the required centripetal force, qvB = mv^2/r, gives the radius r = mv/(qB): faster or more massive particles trace larger circles, stronger fields or larger charge shrink the circle.",
    example: `qvB = mv^2 / r   ->   r = mv / (qB)

Proton: m = 1.67x10^-27 kg, v = 1x10^6 m/s, q = 1.6x10^-19 C, B = 0.5 T
r = (1.67x10^-27)(1x10^6) / [(1.6x10^-19)(0.5)]
r = 1.67x10^-21 / 8x10^-20 ~ 0.0209 m ~ 2.1 cm` },
  { section: "Motion in a Magnetic Field", title: "Cyclotron frequency — independent of speed and radius",
    explanation: "The period of the circular motion is T = 2*pi*r/v = 2*pi*m/(qB) -- notice v and r cancel out, so T (and its inverse, the cyclotron frequency f = qB/2*pi*m) depends only on the charge-to-mass ratio and the field strength, NOT on the particle's speed or the radius of its path. This remarkable independence is what makes the cyclotron accelerator work with a single fixed driving frequency.",
    example: `f = qB / (2 pi m)     (independent of v and r)

Proton in B = 1 T:
f = (1.6x10^-19)(1) / [2 pi (1.67x10^-27)]
f ~ 1.6x10^-19 / 1.049x10^-26 ~ 1.52x10^7 Hz ~ 15.2 MHz` },
  { section: "Motion in a Magnetic Field", title: "Helical motion in a magnetic field",
    explanation: "If a charged particle enters a uniform magnetic field with velocity at some angle to B (neither parallel nor perpendicular), split v into a component v_parallel (along B) and v_perp (perpendicular to B). v_parallel feels no force (since it's parallel to B) and continues unchanged; v_perp produces circular motion of radius r = m.v_perp/(qB). The combined motion is a HELIX that advances along B while circling around it; the axial distance covered per revolution is called the pitch, p = v_parallel x T.",
    example: `v = 1x10^6 m/s at theta = 30 deg to B; B = 0.2 T, proton

v_parallel = v cos30 = 8.66x10^5 m/s
v_perp     = v sin30 = 5.00x10^5 m/s

T = 2 pi m / (qB) = 2 pi (1.67x10^-27) / [(1.6x10^-19)(0.2)] ~ 3.28x10^-7 s
r = m v_perp / (qB) ~ 0.0261 m ~ 2.61 cm
pitch p = v_parallel x T ~ (8.66x10^5)(3.28x10^-7) ~ 0.284 m ~ 28.4 cm` },
  { section: "Motion in a Magnetic Field", title: "The cyclotron — working principle and limitations",
    explanation: "A cyclotron accelerates charged particles using two hollow D-shaped electrodes ('dees') inside a uniform magnetic field, with an oscillating electric field in the narrow gap between them. Each time the particle crosses the gap it gains energy and speeds up, spiraling outward to a larger radius (r = mv/qB grows with v) -- but because the period T = 2*pi*m/(qB) doesn't depend on speed, a FIXED oscillator frequency (equal to the cyclotron frequency) keeps re-accelerating it in step. At very high speeds, relativistic mass increase makes T grow, breaking this synchronism and capping the maximum energy an ordinary cyclotron can deliver.",
    example: `Maximum kinetic energy at maximum radius R (dee radius):
KE_max = (qBR)^2 / (2m)

Proton: B = 1 T, R = 0.5 m, q = 1.6x10^-19 C, m = 1.67x10^-27 kg
qBR = (1.6x10^-19)(1)(0.5) = 8x10^-20
KE_max = (8x10^-20)^2 / [2(1.67x10^-27)] ~ 1.92x10^-12 J ~ 12 MeV` },

  // ── Biot-Savart Law ──────────────────────────────────────────────────────────
  { section: "Biot-Savart Law", title: "Biot-Savart law",
    explanation: "The Biot-Savart law gives the magnetic field dB produced by a small current element I.dl at a point located by the position vector r (with unit vector r-hat) from the element: dB = (mu_0/4*pi) . (I dl x r-hat)/r^2. It is an inverse-square law, structurally similar to Coulomb's law for the E-field of a point charge, with dB's direction perpendicular to the plane containing dl and r-hat (right-hand rule). The constant mu_0/4*pi = 1x10^-7 T.m/A exactly, since mu_0 = 4*pi x 10^-7 T.m/A.",
    example: `dB = (mu_0 / 4 pi) . (I dl sin(theta)) / r^2

Current element I dl = 3 A.m, r = 0.1 m, theta = 90 deg (sin = 1)
dB = (1x10^-7)(3)(1) / (0.1)^2 = (1x10^-7)(3) / 0.01 = 3x10^-5 T = 30 microT` },
  { section: "Biot-Savart Law", title: "Magnetic field due to a long straight current-carrying wire",
    explanation: "Integrating the Biot-Savart law over an infinitely long straight wire carrying current I gives the field at perpendicular distance a from the wire: B = mu_0.I / (2*pi.a). The field lines form concentric circles around the wire, with direction given by the right-hand thumb rule (thumb along current, fingers curl in the direction of B).",
    example: `B = mu_0 I / (2 pi a) = (2x10^-7) I / a     [since mu_0/(2 pi) = 2x10^-7]

I = 10 A, a = 0.05 m
B = (2x10^-7)(10) / (0.05) = 4x10^-5 T = 40 microT` },
  { section: "Biot-Savart Law", title: "Magnetic field at the centre of a circular current loop",
    explanation: "For a circular loop of radius R carrying current I, every current element is the same distance R from the centre and perpendicular to the radius vector there, so integrating the Biot-Savart law around the whole loop gives a clean result: B = mu_0.I / (2R), directed along the loop's axis.",
    example: `B = mu_0 I / (2R)

I = 2 A, R = 0.1 m
B = (4 pi x10^-7)(2) / (2 x 0.1) = (8 pi x10^-7) / 0.2 ~ 1.257x10^-5 T ~ 12.6 microT` },
  { section: "Biot-Savart Law", title: "Magnetic field on the axis of a circular current loop",
    explanation: "At a point on the axis of a circular loop (radius R, current I), at distance x from the centre, the Biot-Savart law gives B = mu_0.I.R^2 / [2(R^2+x^2)^(3/2)]. Setting x = 0 reduces this to the centre-of-loop formula mu_0.I/(2R), confirming consistency between the two results.",
    example: `B(x) = mu_0 I R^2 / [2 (R^2 + x^2)^(3/2)]

I = 2 A, R = 0.1 m, x = 0.1 m  (x = R)
R^2 + x^2 = 0.02;  (0.02)^1.5 ~ 0.002828
B = (4 pi x10^-7)(2)(0.01) / [2(0.002828)] ~ 4.44x10^-6 T ~ 4.44 microT
(check: this equals the centre value 12.6 microT x (1/2)^1.5 = 12.6 x 0.354 ~ 4.44 microT)` },
  { section: "Biot-Savart Law", title: "Magnetic dipole moment of a current loop — far axial field",
    explanation: "A current loop of area A carrying current I behaves like a magnetic dipole with moment m = IA. Far from the loop on its axis (x >> R), the axial field formula simplifies since (R^2+x^2)^(3/2) ~= x^3, giving B ~= mu_0.I.R^2/(2x^3) = (mu_0/4*pi).(2m/x^3) -- the same 1/x^3 falloff and form as the far axial field of an electric dipole, confirming the loop's dipole-like behaviour at large distances.",
    example: `Far axis (x >> R): B ~= (mu_0 / 4 pi) . (2m / x^3),  m = I.pi.R^2

I = 5 A, R = 0.02 m -> m = 5 x pi x (0.02)^2 ~ 6.28x10^-3 A.m^2
At x = 1 m:
B ~= (1x10^-7)(2)(6.28x10^-3) / (1)^3 ~ 1.26x10^-9 T ~ 1.26 nT` },

  // ── Ampere's Circuital Law ───────────────────────────────────────────────────
  { section: "Ampere's Circuital Law", title: "Ampere's circuital law",
    explanation: "Ampere's circuital law states that the line integral of B around any closed path (an 'Amperian loop') equals mu_0 times the net steady current enclosed by that loop: (closed loop) B.dl = mu_0.I_enclosed. The sign of I_enclosed follows the right-hand rule relating the loop's traversal direction to the current direction. Like Gauss's law for the electric field, it is most powerful when symmetry lets B be pulled outside the integral.",
    example: `Ampere's law:  (loop) B . dl = mu_0 . I_enclosed

Used to find B for highly symmetric current distributions:
- straight wire (circular Amperian loop)
- solenoid (rectangular Amperian loop)
- toroid (circular Amperian loop inside the core)` },
  { section: "Ampere's Circuital Law", title: "Field due to a long straight wire — Ampere's law approach",
    explanation: "Choosing a circular Amperian loop of radius a, centred on and perpendicular to a long straight wire carrying current I, symmetry makes B constant in magnitude and always tangential along the loop, so the line integral simplifies to B.(2*pi.a) = mu_0.I, giving B = mu_0.I/(2*pi.a) -- exactly the result obtained earlier from the Biot-Savart law, confirming the two methods agree.",
    example: `B (2 pi a) = mu_0 I   ->   B = mu_0 I / (2 pi a)

I = 10 A, a = 0.05 m
B = (2x10^-7)(10) / (0.05) = 4x10^-5 T = 40 microT   (matches Biot-Savart result)` },
  { section: "Ampere's Circuital Law", title: "Magnetic field inside a solenoid",
    explanation: "For a long, tightly wound solenoid with n turns per unit length carrying current I, a rectangular Amperian loop straddling the solenoid's wall (partly inside, partly outside) gives a field inside of B = mu_0.n.I, uniform and parallel to the axis; the field just outside a long ideal solenoid is essentially zero.",
    example: `B(inside) = mu_0 n I         B(outside) ~= 0

n = 2000 turns/m, I = 3 A
B = (4 pi x10^-7)(2000)(3) ~ 7.54x10^-3 T ~ 7.5 mT` },
  { section: "Ampere's Circuital Law", title: "Magnetic field inside a toroid",
    explanation: "A toroid is a solenoid bent into a ring shape, with N total turns wound on a core of mean radius r, carrying current I. A circular Amperian loop of radius r drawn inside the core encloses current N.I, giving B.(2*pi.r) = mu_0.N.I, so B = mu_0.N.I/(2*pi.r) inside the core. Amperian loops drawn in the central hole or outside the toroid enclose zero net current, so B = 0 in both those regions.",
    example: `B(inside core) = mu_0 N I / (2 pi r)      B(hole or outside) = 0

N = 1000 turns, r = 0.1 m, I = 2 A
B = (2x10^-7)(1000)(2) / (0.1) = (2x10^-7)(20000) = 4x10^-3 T = 4 mT` },

  // ── Torque, Galvanometer & Force Between Currents ───────────────────────────
  { section: "Torque, Galvanometer & Force Between Currents", title: "Force between two parallel current-carrying conductors — definition of the ampere",
    explanation: "Two long parallel wires carrying currents I1 and I2 separated by distance d exert forces on each other (each wire's current sits in the other's magnetic field). The force per unit length is F/L = mu_0.I1.I2/(2*pi.d). Currents in the SAME direction ATTRACT; currents in OPPOSITE directions REPEL. This is the basis of the (historical) definition of the ampere: the current that, flowing in two infinite parallel wires 1 m apart in vacuum, produces a force of exactly 2x10^-7 N per metre of length between them.",
    example: `F/L = mu_0 I1 I2 / (2 pi d) = (2x10^-7) I1 I2 / d

I1 = 5 A, I2 = 3 A, d = 0.02 m
F/L = (2x10^-7)(5)(3) / (0.02) = (2x10^-7)(750) = 1.5x10^-4 N/m` },
  { section: "Torque, Galvanometer & Force Between Currents", title: "Torque on a current loop in a magnetic field",
    explanation: "A current loop of N turns, area A, carrying current I, placed in a uniform magnetic field B, experiences a torque tau = m x B, where the loop's magnetic moment is m = N.I.A (direction given by the right-hand rule, normal to the loop). In magnitude, tau = N.I.A.B.sin(theta), where theta is the angle between the loop's normal (m) and B; torque is maximum when the loop's plane is parallel to B (normal perpendicular to B, theta = 90 deg) and zero when the plane is perpendicular to B.",
    example: `tau = N I A B sin(theta)

N = 50, I = 2 A, A = 0.01 m^2, B = 0.3 T, theta = 90 deg (loop plane || B)
tau = (50)(2)(0.01)(0.3)(1) = 0.3 N.m` },
  { section: "Torque, Galvanometer & Force Between Currents", title: "Moving coil galvanometer — construction and working",
    explanation: "A moving coil galvanometer has a rectangular coil of many turns wound on a light frame, suspended between the curved (concave) pole pieces of a strong permanent magnet with a soft iron cylinder inside the coil. This geometry produces a RADIAL magnetic field, meaning B always lies in the plane of the coil no matter how far it deflects (theta = 90 deg always, so sin(theta) = 1 at every deflection). A restoring spring provides a counter-torque k.theta that balances the deflecting torque N.I.A.B, so at equilibrium N.I.A.B = k.theta, i.e. theta is directly proportional to I -- giving a linear, evenly divided scale.",
    example: `Equilibrium: N I A B = k theta   ->   theta = (NAB/k) . I   (linear in I)

Current sensitivity = NAB / k

N = 100, A = 2x10^-4 m^2, B = 0.2 T, k = 1x10^-6 N.m/rad
Current sensitivity = (100)(2x10^-4)(0.2) / (1x10^-6) = 4000 rad/A
Full-scale deflection of 0.1 rad needs I_g = 0.1 / 4000 = 2.5x10^-5 A = 25 microA` },
  { section: "Torque, Galvanometer & Force Between Currents", title: "Conversion of a galvanometer into an ammeter (shunt resistance)",
    explanation: "A galvanometer (coil resistance G, full-scale deflection current I_g) can be converted into an ammeter reading up to a larger current I by connecting a small SHUNT resistance S in PARALLEL with it. The shunt diverts the bulk of the current (I - I_g) around the coil, letting only I_g pass through it at full-scale; equating the voltage across the parallel combination gives S = I_g.G / (I - I_g). The resulting ammeter has very low net resistance (ideally near zero) so it barely disturbs the circuit it's inserted into (in series).",
    example: `S = I_g G / (I - I_g)

G = 100 ohm, I_g = 1x10^-3 A, desired range I = 1 A
S = (1x10^-3)(100) / (1 - 1x10^-3) = 0.1 / 0.999 ~ 0.1 ohm   (small shunt, in parallel)` },
  { section: "Torque, Galvanometer & Force Between Currents", title: "Conversion of a galvanometer into a voltmeter (series resistance)",
    explanation: "A galvanometer can be converted into a voltmeter reading up to a larger voltage V by connecting a large resistance R in SERIES with it, so that a current of exactly I_g flows through the combination when the full voltage V is applied across it: V = I_g.(G + R), giving R = V/I_g - G. The resulting voltmeter has a very high net resistance (ideally near infinite) so it draws minimal current and barely disturbs the circuit it's connected across (in parallel).",
    example: `R = V / I_g - G

G = 100 ohm, I_g = 1x10^-3 A, desired range V = 10 V
R = (10 / 1x10^-3) - 100 = 10000 - 100 = 9900 ohm   (large resistor, in series)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh19Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh19Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh19Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH19_QUESTIONS : NEET_PHYSICS_CH19_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 19</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Moving Charges and Magnetism</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH19_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH19_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
